import { useEffect, useRef, useState } from "react";
import { EmailSuggestInput } from "../EmailSuggestInput";
import {
  X, Paperclip, Send, Loader2, CalendarClock, Bold, Italic, Underline, Strikethrough,
  List, ListOrdered, Link2, Eraser, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const textToHtml = (t) =>
  (t || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .split("\n").map((l) => (l.trim() ? l : "&nbsp;")).join("<br/>");

export const ComposeModal = ({ api, draft, onClose, onSent, onUndoableSend, hasSignature, draftsFolder, contacts = [], accounts = [], activeEmail = "" }) => {
  const [fromAccount, setFromAccount] = useState(draft?.account || activeEmail);
  const [to, setTo] = useState(draft?.to || "");
  const [cc, setCc] = useState(draft?.cc || "");
  const [bcc, setBcc] = useState(draft?.bcc || "");
  const [showCc, setShowCc] = useState(Boolean(draft?.cc));
  const [showBcc, setShowBcc] = useState(Boolean(draft?.bcc));
  const [subject, setSubject] = useState(draft?.subject || "");
  const [files, setFiles] = useState(draft?.files || []);
  const [includeSig, setIncludeSig] = useState(true);
  const [fromSig, setFromSig] = useState(null); // signature of the selected From mailbox
  const [busy, setBusy] = useState(false);
  const [scheduling, setScheduling] = useState(Boolean(draft?.editScheduledId));
  const [sendAt, setSendAt] = useState(() => {
    if (!draft?.sendAtInit) return "";
    const d = new Date(draft.sendAtInit);
    if (isNaN(d.getTime())) return "";
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
  });
  const [draftUid, setDraftUid] = useState(draft?.draft_uid || "");
  const [savedAt, setSavedAt] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const editorRef = useRef(null);
  const lastSavedRef = useRef("");
  const draftUidRef = useRef(draft?.draft_uid || "");
  const closingRef = useRef(false);

  const getHtml = () => editorRef.current?.innerHTML || "";
  const getText = () => editorRef.current?.innerText || "";
  const snapshot = () => JSON.stringify([to, cc, bcc, subject, fromAccount, getText(), getHtml()]);
  const hasContent = () => Boolean(to.trim() || cc.trim() || bcc.trim() || subject.trim() || getText().trim());

  // prefill the editor (reply/forward quote, or an existing draft)
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (draft?.body_html) el.innerHTML = draft.body_html;
    else if (draft?.body) el.innerHTML = textToHtml(draft.body);
    lastSavedRef.current = JSON.stringify([draft?.to || "", draft?.cc || "", draft?.bcc || "", draft?.subject || "", el.innerText || "", el.innerHTML || ""]);
  }, []);

  // signature auto-picks by the selected From mailbox
  useEffect(() => {
    if (!fromAccount) return;
    let live = true;
    api.get("/mail/signature", { params: { account: fromAccount } })
      .then(({ data }) => { if (live) setFromSig(data.signature || null); })
      .catch(() => { if (live) setFromSig(null); });
    return () => { live = false; };
  }, [fromAccount, api]);

  const saveDraft = async (silent = true) => {
    if (!hasContent() || snapshot() === lastSavedRef.current) return false;
    setSavingDraft(true);
    try {
      const { data } = await api.post("/mail/drafts/save", {
        to, cc, bcc, subject,
        account: fromAccount,
        body: getText(),
        body_html: getHtml(),
        draft_uid: draftUidRef.current,
      });
      if (data.draft_uid) {
        draftUidRef.current = data.draft_uid;
        setDraftUid(data.draft_uid);
      }
      lastSavedRef.current = snapshot();
      setSavedAt(new Date());
      if (!silent) toast.success("Draft saved");
      return true;
    } catch {
      if (!silent) toast.error("Could not save the draft");
      return false;
    } finally {
      setSavingDraft(false);
    }
  };

  // Outlook-style: auto-save only on an idle pause (not during active typing)
  const lastSeenRef = useRef("");
  useEffect(() => {
    lastSeenRef.current = snapshot();
    const t = setInterval(() => {
      const now = snapshot();
      // Only save when content has stopped changing since the last tick (idle pause)
      if (now === lastSeenRef.current) saveDraft(true);
      lastSeenRef.current = now;
    }, 18000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const discardServerDraft = () => {
    const uid = draftUidRef.current;
    const folder = draft?.draftsFolder || draftsFolder;
    if (uid && folder) {
      api.delete(`/mail/message/${uid}`, { params: { folder } }).catch(() => {});
      draftUidRef.current = "";
      setDraftUid("");
    }
  };

  const discard = async () => {
    // Outlook-style trash in the composer: throw the draft away, do NOT save
    discardServerDraft();
    onClose();
  };

  const handleClose = async () => {
    if (closingRef.current) return;
    closingRef.current = true;
    const saved = hasContent() ? await saveDraft(true) : false;
    if (saved) toast.success("Saved to Drafts");
    onClose();
  };

  const exec = (cmd, value = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
  };

  const addLink = () => {
    const url = window.prompt("Link address (https://…)");
    if (url) exec("createLink", url);
  };

  const buildForm = () => {
    const fd = new FormData();
    fd.append("to", to);
    fd.append("cc", cc);
    fd.append("bcc", bcc);
    fd.append("from_account", fromAccount);
    fd.append("subject", subject);
    fd.append("body", getText());
    fd.append("body_html", getHtml());
    fd.append("include_signature", includeSig ? "true" : "false");
    files.forEach((f) => fd.append("attachments", f));
    return fd;
  };

  const send = async (e) => {
    e.preventDefault();
    if (!to.trim()) {
      toast.error("Add at least one recipient");
      return;
    }
    setBusy(true);
    try {
      const fd = buildForm();
      fd.append("send_at", new Date(Date.now() + 11000).toISOString());
      fd.append("undo", "true");
      const { data } = await api.post("/mail/schedule", fd);
      discardServerDraft();
      onUndoableSend?.({ sid: data.id, snapshot: { to, cc, bcc, subject, body_html: getHtml(), files } });
      onSent?.();
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send — try again");
    } finally {
      setBusy(false);
    }
  };

  const schedule = async () => {
    if (!to.trim()) {
      toast.error("Add at least one recipient");
      return;
    }
    if (!sendAt) {
      toast.error("Pick the date and time to send");
      return;
    }
    const when = new Date(sendAt);
    if (isNaN(when.getTime()) || when.getTime() < Date.now() + 60000) {
      toast.error("Pick a future date and time");
      return;
    }
    setBusy(true);
    try {
      const fd = buildForm();
      fd.append("send_at", when.toISOString());
      if (draft?.editScheduledId) {
        await api.post(`/mail/scheduled/${draft.editScheduledId}/edit`, fd);
        toast.success(`Updated — will send ${when.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`);
      } else {
        await api.post("/mail/schedule", fd);
        toast.success(`Scheduled — will auto-send ${when.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`);
      }
      discardServerDraft();
      onSent?.();
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not schedule — try again");
    } finally {
      setBusy(false);
    }
  };

  const input =
    "w-full bg-white border border-ol-line text-ol-text placeholder:text-ol-sub px-4 py-2.5 text-[13px] rounded-[4px] focus:outline-none focus:border-ol-blue transition-colors";
  const toolBtn = "p-2 text-ol-sub hover:text-ol-blue hover:bg-ol-hover rounded-[4px] transition-colors";
  const colors = ["#242424", "#0F6CBD", "#C239B3", "#CA5010"];

  return (
    <div data-lenis-prevent className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6" style={{ fontFamily: '"Segoe UI", system-ui, sans-serif' }} data-testid="compose-modal" onClick={handleClose}>
      <div
        className="w-full max-w-2xl bg-white shadow-2xl rounded-lg max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 bg-ol-blue text-white">
          <h3 className="text-[15px] font-semibold">{draft?.draft_uid ? "Edit draft" : "New message"}</h3>
          <div className="flex items-center gap-3">
            {savingDraft && (
              <span className="flex items-center gap-1.5 text-[11px] text-white/80" data-testid="compose-draft-saving">
                <Loader2 size={11} className="animate-spin" /> Saving…
              </span>
            )}
            {!savingDraft && savedAt && (
              <span className="text-[11px] text-white/80" data-testid="compose-draft-saved">
                Draft saved {savedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <button
              type="button"
              onClick={discard}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-[4px] transition-colors p-1.5"
              aria-label="Discard draft"
              title="Discard — do not save to Drafts"
              data-testid="compose-trash"
            >
              <Trash2 size={16} />
            </button>
            <button onClick={handleClose} className="text-white/80 hover:text-white hover:bg-white/10 rounded-[4px] transition-colors p-1" aria-label="Close composer" data-testid="compose-close">
              <X size={18} />
            </button>
          </div>
        </div>
        <form onSubmit={send} className="flex flex-col flex-1 min-h-0">
          {/* fixed recipients + toolbar */}
          <div className="shrink-0 px-6 pt-5 pb-3 space-y-3 border-b border-ol-line">
          {/* From picker — send as any mailbox attached to this sign-in (Outlook-style) */}
          <div className="flex items-center gap-3 w-full bg-ol-sel border border-ol-blue/40 px-4 py-0 rounded-[4px] overflow-hidden" data-testid="compose-from-row">
            <span className="text-[11px] tracking-[0.1em] uppercase text-ol-blue font-semibold shrink-0">From</span>
            {accounts.length > 1 ? (
              <div className="relative flex-1 min-w-0">
                <select
                  value={fromAccount}
                  onChange={(e) => setFromAccount(e.target.value)}
                  className="w-full min-w-0 bg-transparent text-[13px] font-semibold text-ol-text py-2.5 pr-6 focus:outline-none cursor-pointer appearance-none"
                  data-testid="compose-from-select"
                  title="Choose which mailbox this email is sent from"
                >
                  {accounts.map((a) => (
                    <option key={a.email} value={a.email}>{a.email}</option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-ol-blue text-xs">▾</span>
              </div>
            ) : (
              <span className="flex-1 min-w-0 truncate text-[13px] font-semibold text-ol-text py-2.5" data-testid="compose-from-static">{fromAccount}</span>
            )}
          </div>
          <div className="relative">
            <EmailSuggestInput multi theme="light" extra={contacts} wrapClassName="min-w-0 w-full" type="text" required placeholder="To" className={`${input} pr-24`} value={to} onChange={(e) => setTo(e.target.value)} data-testid="compose-to" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-3 text-[13px]">
              {!showCc && (
                <button type="button" onClick={() => setShowCc(true)} className="text-ol-blue hover:underline transition-colors" data-testid="compose-show-cc">Cc</button>
              )}
              {!showBcc && (
                <button type="button" onClick={() => setShowBcc(true)} className="text-ol-blue hover:underline transition-colors" data-testid="compose-show-bcc">Bcc</button>
              )}
            </span>
          </div>
          {showCc && (
            <EmailSuggestInput multi theme="light" extra={contacts} type="text" placeholder="Cc (optional)" className={input} value={cc} onChange={(e) => setCc(e.target.value)} data-testid="compose-cc" />
          )}
          {showBcc && (
            <EmailSuggestInput multi theme="light" extra={contacts} type="text" placeholder="Bcc (hidden from other recipients)" className={input} value={bcc} onChange={(e) => setBcc(e.target.value)} data-testid="compose-bcc" />
          )}
          <input type="text" placeholder="Subject" className={input} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject" />

          {/* rich text toolbar (Outlook-style formatting) */}
          <div className="flex items-center gap-0.5 border border-ol-line bg-ol-bg px-2 py-1 rounded-[4px] flex-wrap" data-testid="compose-toolbar">
            <button type="button" title="Bold" className={toolBtn} onClick={() => exec("bold")} data-testid="fmt-bold"><Bold size={14} /></button>
            <button type="button" title="Italic" className={toolBtn} onClick={() => exec("italic")} data-testid="fmt-italic"><Italic size={14} /></button>
            <button type="button" title="Underline" className={toolBtn} onClick={() => exec("underline")} data-testid="fmt-underline"><Underline size={14} /></button>
            <button type="button" title="Strikethrough" className={toolBtn} onClick={() => exec("strikeThrough")} data-testid="fmt-strike"><Strikethrough size={14} /></button>
            <span className="w-px h-5 bg-ol-line mx-1" />
            <button type="button" title="Bulleted list" className={toolBtn} onClick={() => exec("insertUnorderedList")} data-testid="fmt-bullets"><List size={14} /></button>
            <button type="button" title="Numbered list" className={toolBtn} onClick={() => exec("insertOrderedList")} data-testid="fmt-numbered"><ListOrdered size={14} /></button>
            <span className="w-px h-5 bg-ol-line mx-1" />
            <button type="button" title="Insert link" className={toolBtn} onClick={addLink} data-testid="fmt-link"><Link2 size={14} /></button>
            {colors.map((c) => (
              <button
                key={c}
                type="button"
                title={`Text color ${c}`}
                className="p-2 hover:bg-ol-hover rounded-[4px] transition-colors"
                onClick={() => exec("foreColor", c)}
                data-testid={`fmt-color-${c.replace("#", "")}`}
              >
                <span className="block w-3.5 h-3.5 rounded-full border border-ol-line" style={{ backgroundColor: c }} />
              </button>
            ))}
            <button type="button" title="Clear formatting" className={toolBtn} onClick={() => exec("removeFormat")} data-testid="fmt-clear"><Eraser size={14} /></button>
          </div>
          </div>{/* end fixed top */}
          {/* scrollable message body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="w-full bg-white text-ol-text px-1 py-1 text-[14px] leading-relaxed min-h-[240px] focus:outline-none"
            data-testid="compose-body"
          />
          </div>{/* end scrollable body */}
          {/* pinned footer */}
          <div className="shrink-0 px-6 py-3 border-t border-ol-line space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-[13px] text-ol-text border border-ol-line hover:border-ol-blue px-4 py-2 rounded-[4px] cursor-pointer transition-colors" data-testid="compose-attach-label">
              <Paperclip size={14} /> Attach files
              <input
                type="file"
                multiple
                className="hidden"
                data-testid="compose-attachments"
                onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
              />
            </label>
            {files.length > 0 && (
              <span className="text-[12px] text-ol-sub" data-testid="compose-attach-count">{files.map((f) => f.name).join(", ")}</span>
            )}
          </div>
          <label className="flex items-center gap-2.5 text-[12px] text-ol-sub cursor-pointer select-none" data-testid="compose-signature-toggle">
            <input
              type="checkbox"
              checked={includeSig}
              onChange={(e) => setIncludeSig(e.target.checked)}
              className="accent-ol-blue w-3.5 h-3.5"
              data-testid="compose-signature-checkbox"
            />
            {fromSig
              ? <>Add <span className="font-semibold text-ol-text">{fromAccount}</span>'s signature ({fromSig.name})</>
              : <>No signature saved for <span className="font-semibold text-ol-text">{fromAccount}</span> — add one with the pen icon in the header</>}
          </label>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={() => setScheduling((s) => !s)}
              className={`flex items-center gap-2 text-[13px] px-4 py-2 rounded-[4px] border transition-colors ${scheduling ? "border-ol-blue text-ol-blue bg-ol-sel" : "border-ol-line text-ol-text hover:bg-ol-hover"}`}
              data-testid="compose-schedule-toggle"
            >
              <CalendarClock size={14} /> {scheduling ? "Cancel scheduling" : "Send later…"}
            </button>
            <div className="flex items-center justify-end gap-3">
              <button type="button" onClick={handleClose} className="text-[13px] text-ol-sub hover:text-ol-text px-4 py-2 transition-colors" data-testid="compose-discard">
                Close
              </button>
              <button
                type="submit"
                disabled={busy}
                className="flex items-center gap-2 bg-ol-blue hover:bg-ol-bluehover text-white font-semibold text-[13px] px-8 py-2 rounded-[4px] transition-colors disabled:opacity-60"
                data-testid="compose-send-button"
              >
                {busy && !scheduling ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {busy && !scheduling ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
          {scheduling && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 border border-ol-blue/30 bg-ol-sel p-4 rounded-[4px]" data-testid="compose-schedule-row">
              <label className="flex-1 text-[12px] text-ol-sub">
                <span className="block text-[10px] tracking-[0.12em] uppercase text-ol-sub mb-1.5">Auto-send on (your local time)</span>
                <input
                  type="datetime-local"
                  className={input}
                  value={sendAt}
                  min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)}
                  onChange={(e) => setSendAt(e.target.value)}
                  data-testid="compose-schedule-at"
                />
              </label>
              <button
                type="button"
                onClick={schedule}
                disabled={busy}
                className="flex items-center justify-center gap-2 bg-ol-blue hover:bg-ol-bluehover text-white font-semibold text-[13px] px-6 py-2 rounded-[4px] transition-colors disabled:opacity-60 sm:mt-5"
                data-testid="compose-schedule-button"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <CalendarClock size={15} />}
                {busy ? "Scheduling…" : "Schedule"}
              </button>
            </div>
          )}
          </div>{/* end pinned footer */}
        </form>
      </div>
    </div>
  );
};
